import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

const SITE_DOMAIN = "antagonista.io";

function isInternal(url) {
  try {
    const { hostname } = new URL(url);
    return hostname === SITE_DOMAIN || hostname === `www.${SITE_DOMAIN}`;
  } catch {
    return false;
  }
}

const PATH_LABELS = {
  "/contact": "Contáctanos",
  "/creative-lab": "Creative Lab",
  "/intelligence-lab": "Intelligence Lab",
  "/portfolio": "Portafolio",
  "/methodology": "Metodología",
  "/pricing": "Precios",
  "/about": "Sobre Antagonista",
  "/blog": "Blog",
  "/services": "Servicios",
};

function friendlyLabel(href) {
  try {
    const { pathname } = new URL(href);
    return (
      PATH_LABELS[pathname] ?? PATH_LABELS[pathname.replace(/\/$/, "")] ?? href
    );
  } catch {
    return href;
  }
}

function renderLink(key, href, explicitLabel) {
  const linkClass =
    "text-accent underline underline-offset-2 hover:text-accent/80 transition-colors";
  const label =
    explicitLabel ?? (isInternal(href) ? friendlyLabel(href) : href);
  if (isInternal(href)) {
    const path = new URL(href).pathname;
    return (
      <Link key={key} to={path} className={linkClass}>
        {label}
      </Link>
    );
  }
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClass}
    >
      {label}
    </a>
  );
}

// Renderiza markdown inline: links, negritas, itálicas, código
function renderInline(text) {
  const parts = [];
  // Orden: markdown link [label](url) primero, luego URL desnuda, luego formato
  // URL desnuda: captura \S+ y luego se le recorta puntuación final
  const pattern =
    /\[(.+?)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/\S+|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));

    if (match[1] !== undefined) {
      // [label](url) — label explícito del autor
      parts.push(renderLink(key++, match[2], match[1]));
    } else if (match[0].startsWith("http")) {
      // URL desnuda — recortar puntuación de cierre de oración al final
      const url = match[0].replace(/[.,!?;:)"'\]]+$/, "");
      // Si recortamos chars, devolver el sobrante como texto
      const tail = match[0].slice(url.length);
      parts.push(renderLink(key++, url, null));
      if (tail) parts.push(tail);
    } else if (match[3] !== undefined) {
      parts.push(
        <strong key={key++} className="font-semibold text-white">
          {match[3]}
        </strong>,
      );
    } else if (match[4] !== undefined) {
      parts.push(
        <em key={key++} className="italic">
          {match[4]}
        </em>,
      );
    } else if (match[5] !== undefined) {
      parts.push(
        <code
          key={key++}
          className="bg-white/10 px-1 py-0.5 rounded text-[11px] font-mono"
        >
          {match[5]}
        </code>,
      );
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Renderiza markdown completo: listas, párrafos con negritas/itálicas
function MarkdownMessage({ content }) {
  const lines = content.split("\n");
  const elements = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc list-inside space-y-0.5 my-1">
          {listItems}
        </ul>,
      );
      listItems = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    // Línea de lista: empieza con "- " o "* "
    if (/^[-*] /.test(trimmed)) {
      const text = trimmed.slice(2);
      listItems.push(
        <li key={key++} className="text-white/85 leading-relaxed">
          {renderInline(text)}
        </li>,
      );
    } else {
      flushList();
      if (trimmed === "") {
        // Línea vacía → separación
        elements.push(<div key={key++} className="h-1" />);
      } else {
        elements.push(
          <p key={key++} className="leading-relaxed">
            {renderInline(trimmed)}
          </p>,
        );
      }
    }
  }
  flushList();

  return <div className="space-y-0.5 text-sm">{elements}</div>;
}

const WELCOME_MESSAGE = {
  role: "assistant",
  content: "Hola, soy el asistente de Antagonista. ¿En qué puedo ayudarte hoy?",
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll al último mensaje
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Foco al abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasUnread(false);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: "user", content: trimmed };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);

    try {
      const isLocal =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const apiBase = isLocal ? "http://localhost:3001" : "";

      const response = await fetch(`${apiBase}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: updatedMessages }),
      });

      const data = await response.json();

      if (data.success && data.reply) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.reply },
        ]);
      } else if (data.error === "rate_limited") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Los servidores están con alta demanda ahora mismo. Intenta de nuevo en un momento.",
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Ocurrió un error al procesar tu mensaje. Por favor intenta de nuevo.",
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sin conexión. Verifica tu red e intenta de nuevo.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Panel de chat */}
      <div
        className={`fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[370px] max-h-[75vh] flex flex-col
          bg-[#0A0A0A] border border-white/10 rounded-lg shadow-2xl
          transition-all duration-300 ease-out
          ${isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
        role="dialog"
        aria-label="Chat con Antagonista"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-sm bg-accent flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[10px] font-bold font-mono tracking-widest leading-none">
                A
              </span>
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">
                Antagonista
              </p>
              <p className="text-white/40 text-[11px] leading-tight">
                Asistente IA
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/40 hover:text-white/80 transition-colors p-1"
            aria-label="Cerrar chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0 scrollbar-thin scrollbar-thumb-white/10">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-accent text-white"
                    : "bg-white/5 text-white/85 border border-white/8"
                }`}
              >
                {msg.role === "user" ? (
                  msg.content
                ) : (
                  <MarkdownMessage content={msg.content} />
                )}
              </div>
            </div>
          ))}

          {/* Indicador de typing */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/8 px-3 py-2.5 rounded flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-white/10">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder="Escribe tu mensaje..."
              rows={1}
              className="flex-1 bg-white/5 border border-white/10 rounded text-white/90 placeholder-white/25
                text-sm px-3 py-2 resize-none focus:outline-none focus:border-accent/60 transition-colors
                disabled:opacity-50 min-h-[38px] max-h-[100px] scrollbar-thin scrollbar-thumb-white/10"
              style={{ lineHeight: "1.5" }}
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              className="flex-shrink-0 w-9 h-9 bg-accent rounded flex items-center justify-center
                hover:bg-accent/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Enviar mensaje"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>

          {/* Disclaimer + Powered by */}
          <p className="text-center text-white/20 text-[10px] mt-2 leading-snug select-none">
            Vortex IA puede cometer errores. No envíes información personal.
          </p>
          <p className="text-center text-white/15 text-[9px] mt-0.5 tracking-wide select-none">
            Powered by Vortex AI
          </p>
        </div>
      </div>

      {/* Botón flotante */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="fixed bottom-5 right-4 sm:right-6 z-50 w-12 h-12 bg-accent hover:bg-accent/85
          rounded-sm shadow-lg flex items-center justify-center transition-all duration-200
          hover:scale-105 active:scale-95"
        aria-label={isOpen ? "Cerrar chat" : "Abrir chat"}
      >
        <span
          className={`absolute transition-all duration-200 ${isOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-45"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>
        <span
          className={`absolute transition-all duration-200 ${isOpen ? "opacity-0 -rotate-45" : "opacity-100 rotate-0"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>

        {/* Badge no leído */}
        {hasUnread && !isOpen && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">1</span>
          </span>
        )}
      </button>
    </>
  );
}
